import { useMemo, useState } from "react";
import { Info, History } from "lucide-react";
import { LightTooltip } from "../../ui/tooltip";
import { InputFields } from "./input-fields";
import { LabelFields } from "./label-fields";
import { parseCondition } from "../../data-driven-form/parseCondition";
import { DROPDOWN_WITH_TEXTFIELD, HORIZONTAL_LINE } from "../../../utils/InputFieldConfig";
import { cn } from "../../../lib/utils";
import { formatDateAndTimeFromISO, humanReadableDate } from "../../../utils/dateHelper";

const COL_WIDTH_MAP = {
  12: "md:w-full",
  9: "md:w-3/4",
  8: "md:w-2/3",
  6: "md:w-1/2",
  4: "md:w-1/3",
  3: "md:w-1/4",
  2: "md:w-1/6",
  1: "md:w-1/12",
};

const getColWidthClass = (xs) => COL_WIDTH_MAP[xs] || "md:w-full";

const findMatchingAuditKey = (audit, prefix, index) => {
  const regexp = /^(\w+)\.(\d+)\.(.+)$/;
  const keys = Object.keys(audit);
  
  for (let i = 0; i < keys.length; i++) {
    const match = keys[i].match(regexp);
    if (match && match[1] === prefix && Number.parseInt(match[2]) >= index) {
      return audit[keys[i]].length > 1 ? audit[keys[i]] : null;
    }
  }
  return null;
};

const getAuditObjectByName = (name, auditData) => {
  const audit = auditData?.audit;
  if (!name || !audit) return null;

  if (audit[name]) {
    return audit[name].length > 1 ? audit[name] : null;
  }

  const [prefix, index] = name.split(".");
  return findMatchingAuditKey(audit, prefix, index);
};

const computeError = (componentContext, pathToValue) => {
  const fieldError = componentContext?.errorMessages?.[pathToValue];
  
  if (!fieldError || fieldError === "") {
    return { isError: false, message: null };
  }
  
  if (typeof fieldError === "object") {
    return {
      isError: false,
      message: null,
      infoContainer: {
        isInfoMessage: true,
        messageType: fieldError?.infoType,
        infoMessage: fieldError?.infoMessage,
      },
    };
  }
  
  return { isError: true, message: fieldError };
};

const checkPermission = (ability, permission) => {
  if (!ability || !permission) return true;
  
  const { action, subject, negatePermission } = permission;
  const canPerform = ability.can(action, subject);
  
  return negatePermission ? !canPerform : canPerform;
};

const getLabelTitle = (item, fieldValue, error, onFieldChange, readOnly, fieldValues, componentContext, handleFieldLevelHandler, currentPiiInfoFields) => {
  const fieldType = item?.type?.toLowerCase();
  
  if (fieldType === HORIZONTAL_LINE || fieldValue === "--") {
    return "";
  }
  
  if (fieldType === DROPDOWN_WITH_TEXTFIELD) {
    return fieldValue;
  }
  
  return String(
    LabelFields(
      fieldValue,
      item,
      error,
      onFieldChange,
      {},
      readOnly,
      fieldValues,
      componentContext,
      handleFieldLevelHandler,
      currentPiiInfoFields
    ) ?? ""
  );
};

const getTooltipSide = (placement) => {
  if (placement?.startsWith("left")) return "left";
  if (placement?.startsWith("top")) return "top";
  if (placement?.startsWith("bottom")) return "bottom";
  return "right";
};

const shouldShowRequiredAsterisk = (item, readOnly, removeAestrik) => {
  return item?.required && !item?.hideLabel && !readOnly && !removeAestrik;
};

const HistoryTooltip = ({ steps }) => {
  const [showAll, setShowAll] = useState(false);
  const VISIBLE_COUNT = 3;
  const visibleSteps = showAll ? steps : steps?.slice(0, VISIBLE_COUNT);
  const hasMore = steps?.length > VISIBLE_COUNT;

  return (
    <LightTooltip
      title={
        <div className="w-[280px]">
          <p className="font-bold text-[16px] text-gray-900 mb-4">Edit history</p>

          <div
            className={cn(showAll && hasMore ? "max-h-[280px] overflow-y-auto" : "")}
            style={showAll && hasMore ? { scrollbarWidth: "none", msOverflowStyle: "none" } : undefined}
          >
            {visibleSteps?.map((s, i) => {
              const isLast = i === visibleSteps.length - 1;
              const date = humanReadableDate(s?.timestamp, true);
              return (
                <div key={`${s?.actor}-${i}`} className="flex gap-3">
                  <div className="flex flex-col items-center w-[22px] flex-shrink-0">
                    {i === 0 ? (
                      <div className="mt-[1px] h-[22px] w-[22px] rounded-full border-2 border-[#8B7A3D] bg-white flex items-center justify-center">
                        <div className="h-[10px] w-[10px] rounded-full bg-[#8B7A3D]" />
                      </div>
                    ) : (
                      <div className="mt-[1px] h-[22px] w-[22px] rounded-full border-2 border-gray-300 bg-white" />
                    )}
                    {!isLast && <div className="w-[2px] flex-1 min-h-[12px] bg-gray-300" />}
                  </div>
                  <div className={cn("flex flex-col min-w-0 pt-[1px]", isLast ? "" : "pb-3")}>
                    <span className="font-semibold text-[13px] text-gray-900 leading-snug">
                      {s?.value}
                    </span>
                    <span className="text-[11px] text-gray-500 mt-[2px]">
                      {s?.actorName ?? s?.actor} • {date}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>

          {hasMore && (
            <button
              type="button"
              onClick={() => setShowAll((prev) => !prev)}
              className="mt-2 text-[12px] font-semibold text-[#8B7A3D] hover:underline focus:outline-none"
            >
              {showAll ? "View less" : "View more"}
            </button>
          )}
        </div>
      }
      side="right"
      className="!p-5 !shadow-lg !rounded-xl"
    >
      <button
        className="ml-1 inline-flex h-5 w-5 items-center justify-center rounded hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-300"
        aria-label="Show history"
        type="button"
      >
        <History className="h-4 w-4 text-gray-700" />
      </button>
    </LightTooltip>
  );
};

const InfoTooltip = ({ item }) => (
  <LightTooltip
    title={<div className="text-sm">{item?.infoData}</div>}
    side={getTooltipSide(item?.tooltipPlacement)}
  >
    <button
      className="ml-1 inline-flex h-5 w-5 items-center justify-center rounded hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-300"
      aria-label="Info"
      type="button"
    >
      <Info className="h-4 w-4" style={{ color: item?.infoIconColor || "#000000" }} />
    </button>
  </LightTooltip>
);

const RequiredAsterisk = () => (
  <span className="relative -top-[6px] right-[7px] text-red-500 ml-1">*</span>
);

const FieldLabel = ({ item, readOnly, customClasses, steps, removeAestrik }) => (
  <div className={cn("flex items-center gap-2", readOnly ? customClasses?.labelRow : "")}>
    <label
      className={cn(
        readOnly ? "block text-xs font-normal text-neutral-7 mb-1" : "block text-sm font-medium mb-1",
        customClasses?.label
      )}
    >
      {item.label}
      {item.optional && (
        <span className="text-neutral-7 ml-1 text-sm font-medium">(Optional)</span>
      )}
    </label>
    {readOnly && steps?.length > 1 && <HistoryTooltip steps={steps} />}
    {item?.infoIcon && item?.infoData && <InfoTooltip item={item} />}
    {shouldShowRequiredAsterisk(item, readOnly, removeAestrik) && <RequiredAsterisk />}
  </div>
);

const ReadOnlyValue = ({ fieldValue, item, error, onFieldChange, readOnly, fieldValues, componentContext, handleFieldLevelHandler, currentPiiInfoFields, isContainer, customClasses, labelTitle }) => (
  <span className="block">
    <span
      className={cn(
        "inline-block py-1 w-full",
        isContainer ? "flex" : "",
        customClasses?.readLabelValue || "truncate"
      )}
      title={labelTitle}
    >
      {LabelFields(fieldValue, item, error, onFieldChange, {}, readOnly, fieldValues, componentContext, handleFieldLevelHandler, currentPiiInfoFields)}
    </span>
  </span>
);

const getValueRowStyle = (stickyContainer, errorMessage) => {
  if (!stickyContainer) return {};
  return { paddingBottom: errorMessage ? 0 : "20px" };
};

export const FormRenderer = (props) => {
  const {
    item,
    onFieldChange,
    onFieldBlur,
    fieldValue,
    componentContext,
    fieldValues,
    isSubField,
    ability,
    readOnly,
    autoFocus,
    auditData,
    customClasses = {},
    handleFieldLevelHandler,
    currentPiiInfoFields,
    supplementAddressData = {},
    highlightField = false,
    stickyContainer = false,
    removeAestrik = false
  } = props;

  const { xs = 3, condition } = item || {};
  const { visible } = parseCondition(condition, componentContext, fieldValue);
  const steps = getAuditObjectByName(item?.pathToValue, auditData);
  const error = useMemo(() => computeError(componentContext, item?.pathToValue), [componentContext, item?.pathToValue]);

  if (!visible || item?.hidden) return <></>;
  if (!checkPermission(ability, item?.permission)) return <></>;

  const isContainer = item?.type?.toLowerCase() === "container";
  const widthClass = getColWidthClass(xs);
  const labelTitle = getLabelTitle(item, fieldValue, error, onFieldChange, readOnly, fieldValues, componentContext, handleFieldLevelHandler, currentPiiInfoFields);

  return (
    <div className={cn("w-full", isContainer ? "py-0" : "py-2", isSubField ? "px-1" : "px-2", widthClass, isSubField && "pt-4", customClasses?.container)}>
      <div className={cn("flex flex-col gap-2", highlightField && "rounded-md ring-2 ring-gray-300 p-2", customClasses?.innerContainer)}>
        {item?.label ? (
          <FieldLabel item={item} readOnly={readOnly} customClasses={customClasses} steps={steps} removeAestrik={removeAestrik} />
        ) : (
          !item?.hideLabel && <span className="opacity-0">No Label</span>
        )}

        <div className={isContainer ? "flex flex-col" : ""} style={getValueRowStyle(stickyContainer, error?.message)}>
          {readOnly ? (
            <ReadOnlyValue
              fieldValue={fieldValue}
              item={item}
              error={error}
              onFieldChange={onFieldChange}
              readOnly={readOnly}
              fieldValues={fieldValues}
              componentContext={componentContext}
              handleFieldLevelHandler={handleFieldLevelHandler}
              currentPiiInfoFields={currentPiiInfoFields}
              isContainer={isContainer}
              customClasses={customClasses}
              labelTitle={labelTitle}
            />
          ) : (
            <InputFields
              fieldValue={fieldValue}
              item={item}
              error={error}
              onFieldChange={onFieldChange}
              fieldValues={fieldValues}
              onFieldBlur={onFieldBlur}
              disabled={item?.disabled}
              autoFocus={autoFocus}
              customClasses={customClasses}
              currentPiiInfoFields={currentPiiInfoFields}
              handleFieldLevelHandler={handleFieldLevelHandler}
              supplementAddressData={supplementAddressData}
            />
          )}
        </div>
      </div>
    </div>
  );
};
